import React from 'react';
import { TD } from './tableStyles';
import { inr, num, pct } from '../format';
import type { Totals } from '../stats';
import type { StageStatus } from '../types';

const money = (value: number, orders: number) => (orders > 0 ? `${inr(value)} (${num(orders)})` : '—');

/** Opens the orders behind a cell: null = every order in the cell's agent / bucket, else one delivery stage. */
export type DrillOrders = (status: StageStatus | null, what: string, total: number) => void;

/** The eleven commercial cells for one agent, one agent x bucket, or the grand total. Every count opens the orders behind it. */
/** What the cells read. A Totals fits, and so does any row that carries the same fields (Category / Product / Agent). */
export type CommercialTotals = Pick<Totals, 'orders' | 'convPct' | 'booked' | 'stages' | 'deliveryPct' | 'inTransitPct' | 'rtoPct' | 'bookedAov'>;

export const CommercialCells: React.FC<{ t: CommercialTotals; drill?: DrillOrders }> = ({ t, drill }) => {
  const c = `${TD} text-right tabular-nums whitespace-nowrap`;
  const st = t.stages;
  const link = (text: string, orders: number, status: StageStatus | null, what: string) =>
    drill && orders > 0 ? (
      <button type="button" onClick={e => { e.stopPropagation(); drill(status, what, orders); }}
        className="underline decoration-dotted decoration-slate-300 underline-offset-4 hover:decoration-violet-400">{text}</button>
    ) : text;
  return (
    <>
      <td className={c}>{link(num(t.orders), t.orders, null, 'orders')}</td>
      <td className={c} title="Orders ÷ unique customers worked in this bucket">{pct(t.convPct)}</td>
      <td className={`${c} font-semibold`}>{link(money(t.booked, t.orders), t.orders, null, 'booked')}</td>
      <td className={`${c} !text-emerald-700 dark:!text-emerald-300`}>{link(money(st.delivered.value, st.delivered.orders), st.delivered.orders, 'st_delivered', 'delivered')}</td>
      <td className={c}>{link(money(st.inTransit.value, st.inTransit.orders), st.inTransit.orders, 'st_in_transit', 'in transit')}</td>
      <td className={c}>{link(money(st.awaitingPickup.value, st.awaitingPickup.orders), st.awaitingPickup.orders, 'st_awaiting_pickup', 'awaiting pickup')}</td>
      <td className={`${c} !text-red-600`}>{link(money(st.returned.value, st.returned.orders), st.returned.orders, 'st_returned', 'returned')}</td>
      <td className={c}>{link(money(st.cancelled.value, st.cancelled.orders), st.cancelled.orders, 'st_cancelled', 'cancelled')}</td>
      <td className={`${c} ${t.deliveryPct !== null && t.deliveryPct < 50 ? 'text-rose-600 dark:text-rose-300' : ''}`} title="Delivered ÷ shipped (delivered + in transit + returned)">{pct(t.deliveryPct)}</td>
      <td className={c} title="In transit ÷ shipped (delivered + in transit + returned)">{pct(t.inTransitPct)}</td>
      <td className={`${c} !text-red-600`} title="Returned ÷ shipped (delivered + in transit + returned)">{pct(t.rtoPct)}</td>
      <td className={c}>{t.bookedAov === null ? '—' : inr(t.bookedAov)}</td>
    </>
  );
};

/** The commercial column labels, in the order CommercialCells renders them. */
export const COMMERCIAL_LABELS = ['Orders', 'Conv%', 'Booked', 'Delivered', 'In Transit', 'Awaiting Pickup', 'Returned', 'Cancelled', 'Delivery %', 'In Transit %', 'RTO %', 'AOV'];

export default CommercialCells;
